# Microservice
enum GeneralInsuranceQuoteStage {
  GI_PRODUCT_STATUS_INVALID
  PROPOSED
  SELECTED
  APPLICATION_IN_PROGRESS
  CURRENT
  EXPIRED
  APPLICATION_STARTED
  APPLICATION_COMPLETE
  CANCELLED
  ARCHIVED
  DECLINED
}

enum GeneralInsuranceProductTier {
  INVALID_PRODUCT_TIER
  GOLD
  SILVER
  BRONZE
  STANDARD
  PREMIUM
}

enum GeneralInsuranceProvider {
  INVALID_EXTERNAL_SYSTEM
  PAYMENTSHIELD
  UINSURE
  PS
  LV
  HIGOS
  TOWERGATE
  BENNETT_ALEXANDER
  GALLAGHER
  THE_SOURCE
  ASSURANT
  MANUAL
}

input GeneralInsuranceAuthCreds {
  encrypted_username: String!
  encrypted_password: String!
  provider: GeneralInsuranceProvider!
}

enum ProviderId {
  SBG_PROVIDER_ID_INVALID
  SBG_PROVIDER_ID_CETA
  SBG_PROVIDER_ID_PAYM
  SBG_PROVIDER_ID_UINS
}

enum GeneralInsuranceType {
  INVALID_GI_PRODUCT_TYPE
  BUILDINGS
  CONTENTS_ONLY
  BUILDINGS_AND_CONTENTS
  LANDLORDS_CONTENTS
  BUILDINGS_AND_LANDLORDS_CONTENTS
  TERRORISM
  FLOOD
  OTHER
}

enum GeneralInsurancePremiumFrequency {
  INVALID_PREMIUM_FREQUENCY
  PREMIUM_FREQUENCY_MONTHLY
  PREMIUM_FREQUENCY_ANNUAL
}

enum GeneralInsuranceYearsNoClaims {
  INVALID_YEARS_NO_CLAIMS
  ZERO
  ONE
  TWO
  THREE
  FOUR
  FIVE
  SIX
  SEVEN
  EIGHT
  NINE
  TEN
  LONGER
}

enum SpecifiedItemType {
  INVALID_SPECIFIED_ITEM_TYPE
  CLOCKS
  COIN_COLLECTION
  CUPS_SHEILDS_TROPHIES_AND_MASONIC_REGALIA
  CURIOS
  FURS
  GOLD_ITEMS
  GUNS
  JEWELLERY_WATCHES
  MEDAL_COLLECTION
  MUSICAL_INSTRUMENTS_AMATEUR
  PAINTINGS
  PEARLS
  PHOTOGRAPHIC_EQUIPMENT_AMATEUR
  PIANOS
  PICTURES
  PRECIOUS_METALS
  SCULPTURES
  SILVER_ITEMS
  SPORTING_GUNS_AND_SHOOTING_EQUIPMENT
  STAMP_COLLECTION
  STATUES
  TAPESTRIES
  WORKS_OF_ART
  CLOTHING
  CONTACT_LENSES
  DENTURES_INCLUDING_CROWNS
  ELECTRIC_WHEELCHAIRS
  GOKART
  GOLF_BUGGY
  HEARING_AIDS
  HOME_COMPUTER_EQUIPMENT_EG_LAPTOP
  MEDICAL_EQUIPMENT
  MODEL_AIRCRAFT
  PEDAL_CYCLES_OVER_1000
  PORTABLE_ELECTRONIC_ITEM_EG_IPOD_IPAD
  PRAMS_AND_PUSHCHAIRS
  SOUND_EQUIPMENT
  SPECTACLES
  SPORTS_EQUIPMENT_EXCLUDING_WINTER_SPORTS_AND_PEDAL_CYCLES
  SPORTS_EQUIPMENT_WINTER_SPORTS
  WHEELCHAIRS_SELF_PROPELLED
  MOBILE_PHONE
}

type GetGeneralInsuranceQuotesResponse {
  quotes: [GeneralInsuranceQuote!]
  excludes_panels: [ExcludedPanels!]
}

type GetGeneralInsuranceQuoteResponse {
  quote: GeneralInsuranceQuote
}

type GeneralInsuranceQuote {
  quote_id: ID # quote_reference
  external_system: String # GeneralInsuranceProvider list to be confirmed
  external_system_quote_uri: String
  policy_reference: String
  details: GeneralInsuranceQuoteDetails
}

type ExcludedPanels {
  incorrect_credentials: Boolean
  missing_credentials: Boolean
  name: String!
  no_valid_quotes_returned: Boolean
  quote_not_requested: Boolean
  server_error: Boolean
  timeout: Boolean
  message: String
}

type GeneralInsuranceQuoteDetails {
  quote_stage: GeneralInsuranceQuoteStage
  product_price_annual: String
  product_price_monthly: String
  product_tier: String # GeneralInsuranceProductTier list to be confirmed
  insurer_name: String
  product_name: String
  assumptions: GeneralInsuranceQuoteAssumptionsDetails
  cover_details: GeneralInsuranceQuoteCoverDetails
  status: GeneralInsuranceQuoteStage
  warnings: [String!]
}

type GeneralInsuranceQuoteAssumptionsDetails {
  is_first_time_buyer: Boolean
  unapproved_mortgage_application: Boolean
  permanently_occupied: Boolean
  subsidence: Boolean
  convictions: Boolean
  insurance_declines: Boolean
  flooding: Boolean
  unoccupied_sixty_days: Boolean
  business_purposes: Boolean
  number_of_adults: Int
  number_of_children: Int
  number_of_bathrooms: Int
  wall_construction: String # PropertyWallType list to be confirmed
  roof_construction: String # PropertyRoofType list to be confirmed
  window_locks_fitted: Boolean
  contents_items_to_specify: Boolean
  external_door_locks: String # not created yet implemented
  alarm_type: String # not created yet implemented
  property_unoccupied: String # not created yet implemented
}

type GeneralInsuranceQuoteCoverDetails {
  buildings_cover_level: Int
  contents_cover_level: Int
  excess_buildings: Int
  excess_contents: Int
  accidental_damage_buildings: Boolean
  accidental_damage_contents: Boolean
  home_emergency_cover_premium: Boolean
  legal_expenses: Boolean
  personal_possessions_total_amount: Int
}

input SpecificItemInput {
  ItemType: String
  ItemDescription: String
  ItemValue: Int
  CoveredAtHome: Boolean
  CoveredOutsideTheHome: Boolean
}

input GeneralInsuranceInput {
  caseID: ID
  includeBuildingsInsurance: Boolean
  includeAccidentalCover: Boolean
  claimFreeYears: Int
  includeContentsPlus: Boolean
  includePersonalPossessionsCover: Boolean
  contentsPersonalUnspecifiedValue: Int
  contentsPersonalPossessions: [SpecificItemInput]
  desiredExcess: Int
  desiredBuildingsExcessIfDifferent: Int
  includeFamilyLegalProtection: Boolean
  desiredCommission: Int
  includeHomeEmergencyCover: Boolean
  # landlord fields
  rentGuarantee: Boolean
  manufactureOfDrugsCover: Boolean
  tenantsWillBeStudents: Boolean
  tenantsWillBeOnBenefits: Boolean
  moreThanSixTenants: Boolean
  propertyContainsBedsits: Boolean
  directTenancyAgreement: Boolean
  tenantsAreOver18: Boolean
  tenancyRequirementsMet: Boolean
  includeContentsBTL: Boolean
  includeContents: Boolean
}

input GetGeneralInsuranceQuoteInput {
  case_id: String!
  quote_id: ID! # quote_reference
}

# Luther

type SpecifiedItem {
  type: SpecifiedItemType
  description: String
  value: Int
  include_at_home: Boolean
  include_away_from_home: Boolean
}

type ExternalGeneralInsuranceSource {
  id: String
  name: ExternalIdNames
}

type ExternalGeneralInsurance {
  id: String
  source: ExternalGeneralInsuranceSource
}

type CreateGeneralInsuranceProductDetails {
  quote_reference: ID
  status: GeneralInsuranceQuoteStage
  premium_amount: String
  premium_frequency: GeneralInsurancePremiumFrequency
  term: Int
  provider_id: ProviderId
  buildings_cover_level: String
  contents_cover_level: String
  type: GeneralInsuranceType
  provider: String
  policy_number: String
  underwriter: String
  annual_premium: Int
  commission: Int
  excess: Int
  buildings_excess_if_different: Int
  start_date: String
  end_date: String
  specified_rebuild_cost: Int
  rental_cover: Int
  includes_accidental_damage: Boolean
  accidental_damage_premium: Int
  includes_home_emergency_cover: Boolean
  home_emergency_premium: Int
  includes_legal_cover: Boolean
  legal_cover_premium: Int
  includes_personal_possessions_cover: Boolean
  personal_possessions_sum_assured: Int
  personal_possessions_premium: Int
  years_no_claims: GeneralInsuranceYearsNoClaims
  buildings_years_no_claims_if_different: GeneralInsuranceYearsNoClaims
  specified_items: [SpecifiedItem]
  external: [ExternalGeneralInsurance]
}

type CreateGeneralInsuranceProduct {
  general_insurance_product_id: ID #  Case insurance product reference. Populated automatically; cannot be set.
  client_ids: [ID!]
  case_ids: [ID!]
  details: CreateGeneralInsuranceProductDetails
  property_ids: [ID!]
}

type GeneralInsuranceProductResponse {
  general_insurance_product: CreateGeneralInsuranceProduct
}

type GeneralInsuranceProductCaseResponse {
  case_id: String
  general_insurance_product_id: String
}

type GeneralInsuranceProductsResponse {
  general_insurance_products: [CreateGeneralInsuranceProduct!]
}

input SpecifiedItemInput {
  type: SpecifiedItemType
  description: String
  value: Int
  include_at_home: Boolean
  include_away_from_home: Boolean
}

input GetGeneralInsuranceProductsInput {
  general_insurance_product_ids: [ID!]
  filter_property_ids: [ID!]
  filter_client_ids: [ID!]
  filter_case_ids: [ID!]
}

input CreateGeneralInsuranceProductRequestInput {
  general_insurance_product: CreateGeneralInsuranceProductInput
}

input AddGeneralInsuranceProductCaseRequestInput {
  case_id: String!
  general_insurance_product_id: String!
}

input CreateGeneralInsuranceProductInput {
  general_insurance_product_id: ID
  client_ids: [ID!]
  case_ids: [ID!]
  details: CreateGeneralInsuranceProductDetailsInput
  property_ids: [ID!]
}

input ExternalGeneralInsuranceSourceInput {
  id: String
  name: ExternalIdNames
}

input ExternalGeneralInsuranceInput {
  id: String
  source: ExternalGeneralInsuranceSourceInput
}

input CreateGeneralInsuranceProductDetailsInput {
  quote_reference: ID #quote_id
  status: GeneralInsuranceQuoteStage
  premium_amount: String
  premium_frequency: GeneralInsurancePremiumFrequency
  term: Int
  provider_id: ProviderId
  buildings_cover_level: String
  contents_cover_level: String
  type: GeneralInsuranceType
  provider: String
  policy_number: String
  underwriter: String
  annual_premium: Int
  commission: Int
  excess: Int
  buildings_excess_if_different: Int
  start_date: String
  end_date: String
  specified_rebuild_cost: Int
  rental_cover: Int
  includes_accidental_damage: Boolean
  accidental_damage_premium: Int
  includes_home_emergency_cover: Boolean
  home_emergency_premium: Int
  includes_legal_cover: Boolean
  legal_cover_premium: Int
  includes_personal_possessions_cover: Boolean
  personal_possessions_sum_assured: Int
  personal_possessions_premium: Int
  manufacture_of_drugs_cover: Boolean
  years_no_claims: GeneralInsuranceYearsNoClaims
  buildings_years_no_claims_if_different: GeneralInsuranceYearsNoClaims
  specified_items: [SpecifiedItemInput]
  external: [ExternalGeneralInsuranceInput]
}
